import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { TrendingUp, AlertTriangle, CircleDollarSign, Boxes, Rocket, CheckCircle2, Circle } from "lucide-react";
import { MODULE_CARDS, TileGrid } from "./tiles";

// Must stay 1:1 with backend onboarding `visible_steps` (WiPay is optional and intentionally absent).
const ONB_STEPS = [
  { key: "business_setup", label: "Business Profile", to: "/settings" },
  { key: "owner_activated", label: "Owner Account", to: "/security" },
  { key: "inventory_started", label: "Inventory Started", to: "/inventory" },
  { key: "employees_configured", label: "Employees", to: "/staff" },
  { key: "tutorial_completed", label: "Tutorial", to: "/help" },
];

function Kpi({ label, value, icon: Icon, accent, sub, testid }) {
  return (
    <Card className="p-5 relative overflow-hidden" data-testid={testid}>
      <div className={`absolute -right-4 -top-4 w-20 h-20 rounded-full ${accent} opacity-10`} />
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon className="w-5 h-5 text-muted-foreground" />
      </div>
      <p className="mono text-2xl lg:text-3xl font-bold mt-2">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

function OnboardingChecklist({ onb }) {
  const nav = useNavigate();
  return (
    <Card className="p-6 border-emerald-500/30" data-testid="onboarding-checklist">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="font-head font-bold flex items-center gap-2"><Rocket className="w-5 h-5 text-emerald-500" /> Finish setting up your business</p>
          <p className="text-sm text-muted-foreground">A few quick steps to get launch-ready. Payment integrations like WiPay are optional and can be set up anytime.</p>
        </div>
        <div className="text-right">
          <p className="mono text-2xl font-bold text-emerald-500" data-testid="onboarding-percent">{onb.percent}%</p>
          <p className="text-xs text-muted-foreground">complete</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {ONB_STEPS.map((s) => {
          const done = onb.milestones?.[s.key];
          return (
            <button key={s.key} onClick={() => nav(s.to)} data-testid={`onboarding-${s.key}`}
              className={`flex items-center gap-2 rounded-xl border p-3 text-left text-sm transition-colors
                ${done ? "border-emerald-500/30 bg-emerald-500/5" : "border-border hover:border-emerald-500/40"}`}>
              {done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 text-muted-foreground shrink-0" />}
              <span className={done ? "font-medium" : "text-muted-foreground"}>{s.label}</span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// Owner: the only dashboard that renders monetary KPIs. The server only sends these keys to the owner role.
export default function OwnerDashboard({ stats }) {
  const { user } = useAuth();
  const [onb, setOnb] = useState(null);
  useEffect(() => {
    if (user?.role === "owner") api.get("/onboarding/status").then((r) => setOnb(r.data)).catch(() => {});
  }, [user]);

  return (
    <div className="space-y-8" data-testid="owner-dashboard">
      {onb && onb.percent < 100 && <OnboardingChecklist onb={onb} />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Today's Sales" value={money(stats?.today_sales)} icon={TrendingUp} accent="bg-emerald-500" testid="kpi-today-sales" />
        <Kpi label="Accounts Receivable" value={money(stats?.total_receivable)} icon={CircleDollarSign} accent="bg-violet-500" testid="kpi-receivable"
          sub={`${stats?.active_credit_accounts || 0} active credit accounts`} />
        <Kpi label="Total Overdue" value={money(stats?.total_overdue)} icon={AlertTriangle} accent="bg-rose-500" testid="kpi-overdue" />
        <Kpi label="Stock Alerts" value={`${(stats?.low_stock || 0) + (stats?.out_of_stock || 0)}`} icon={Boxes} accent="bg-amber-500" testid="kpi-stock-alerts"
          sub={`${stats?.low_stock || 0} low · ${stats?.out_of_stock || 0} out`} />
      </div>
      <TileGrid title="Business Modules" items={MODULE_CARDS} testid="business-modules" />
    </div>
  );
}
